import {RWSService} from '@rws-framework/client';
import BreadService, {type BreadServiceInstance} from "./BreadService";
import ContentsService, {type ContentsServiceInstance} from "./ContentsService";

class StorageService extends RWSService {
  static _IN_CLIENT: boolean = true;
  private data: any;
  private status: any;
  private pending: any[];
  private requires: Record<string, string[]>;
  private resources: Record<string, string>;

  constructor(
    @BreadService private breadService: BreadServiceInstance,
    @ContentsService private contentsService: ContentsServiceInstance,
  ) {
    super();
    this.requires = {
      lesson: ["course"],
      page: ["course", "lesson"],
    };

    this.resources = {
      course: "courses",
      lesson: "lessons",
      page: "pages",
      component: "components",
      notes: "notes",
      prop: "props",
    };

    this.data = {
      courses: {},
      lessons: {},
      pages: {},
      components: {},
      props: {},
      notes: {},
      watchers: {
        prop: {},
      },
    };

    this.status = {
      courses: {},
      lessons: {},
      pages: {},
      components: {},
      notes: {},
      props: {},
    };

    this.pending = [];
  }

  getKeyByValue(obj: any, value: any): string | undefined {
    for (const prop in obj) {
      if (obj.hasOwnProperty(prop) && obj[prop] === value) {
        return prop;
      }
    }
    return undefined;
  };

  private clean<T>(arr: T[], deleteValue: T): T[] {
    return arr.filter((item) => item !== deleteValue);
  }

  public addWatcher(name, resource, callback: Function): boolean {
    if (typeof name !== "string" || typeof resource !== "string") {
      return false;
    }

    const params = resource.split(":");

    if (params[0] === "prop") {
      this.data.watchers.prop[params[1]] ??= {};
      this.data.watchers.prop[params[1]][name] = callback;
    } else {
      this.data.watchers[resource] ??= {};
      this.data.watchers[resource][name] = callback;
    }

    return true;
  }

  public removeWatcher(name, resource): boolean {
    if (typeof name !== "string" || typeof resource !== "string") {
      return false;
    }

    const params = resource.split(":");

    if (params[0] === "prop") {
      delete this.data.watchers.prop[params[1]][name];
    } else {
      delete this.data.watchers[resource][name];
    }

    return true;
  }

  public objectExists(resource: string, name: string): boolean {
    return typeof this.data[this.resources[resource]][name] !== "undefined";
  }

  //set props and notes
  public set(resource: string, name: string, data: any): any {
    if (typeof resource !== "string" || typeof name !== "string") {
      return { err: "Invalid parameters." };
    }

    if (!Object.keys(this.resources).includes(resource)) {
      return { err: "Invalid resource ID." };
    }

    this.data[this.resources[resource]][name] = data;

    if (resource === "prop") {
      this.runWatchers(`${resource}:${name}`);
      this.status[this.resources[resource]][name] = "cached";
    } else {
      this.runWatchers(resource);
    }

    return data;
  }

  public getAll(): any {
    return this.data;
  }

  public clearSome(): void {
    this.status.lessons = {};
    this.status.pages = {};
    this.status.courses = {};
    this.breadService.bread();
    if (window.receiveMessageQ) {
      Object.keys(window.receiveMessageQ).forEach((f) => {
        window.removeEventListener("message", window.receiveMessageQ[f], true);
      });
    }
    window.receiveMessageQ = {};
  }

  public clearAll(): this {
    this.data = {
      courses: {},
      lessons: {},
      pages: {},
      components: {},
      notes: {},
      props: {},
      watchers: {
        prop: {},
      },
    };

    this.status = {
      courses: {},
      lessons: {},
      pages: {},
      components: {},
      notes: {},
      props: {},
    };

    this.breadService.bread();
    if (window["receiveMessageQ"]) {
      Object.keys(window["receiveMessageQ"]).forEach((f) => {
        window.removeEventListener("message", window["receiveMessageQ"][f], true);
      });
    }
    window["receiveMessageQ"] = {};
    return this;
  }

  public getStatus(resource: string, context: any): string | undefined {
    const stat = this.status[this.resources[resource]][context[this.getContextName(resource)]];

    if (typeof stat === "string" && stat.startsWith("cached")) {
      if (context.courseId != stat.substring(6, 30)) return "";
      const timestamp = parseInt(stat.substring(30));
      const elapsed = Date.now() - timestamp;
      return elapsed < 3600000 ? "cached" : "";
    }

    return stat;
  }

  public getAsync<T = any>(resource: string, context: any): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.get(
        resource,
        context,
        (result: any) => {
          if (
            result &&
            typeof result === "object" &&
            "err" in result &&
            Object.keys(result).length === 1
          ) {
            reject(new Error(result.err));
            return;
          }
          resolve(result);
        },
        (err: any) => {
          reject(err instanceof Error ? err : new Error(err?.err ?? String(err)));
        },
      );
    });
  }

  public get(resource: string, context: any, callback: Function = () => {}, error: Function = () => {}) {
    if (!Object.keys(this.resources).includes(resource)) {
      callback({ err: "Invalid resource ID." });
      return;
    }
    if (resource === "prop" || resource === "notes") {
      const correct_resource = resource === 'prop' ? 'props' : 'notes';
      const data = this.data[correct_resource]?.[context];
      if (typeof context !== "string") {
        const error = {
          err: "Property name must be string."
        };

        callback(error);
        return error;
      }
      callback(data);
      return data;
    }

    // Handle remaining logic for other resources.
    const stat = this.getStatus(resource, context);
    if (stat === "cached" && this.contentsService.getCourseMode(context.courseId) !== "edit") {
      callback(this.data[this.resources[resource]][context[this.getContextName(resource)]]);
      return;
    }
    if (stat === "pending") {
      this.pending.push({
        resource: resource,
        context: context,
        callback: callback,
        error: error
      });
      return;
    }

    if (typeof this.requires[resource] === "undefined") {
      this.requires[resource] = [];
    }
    this.requires[resource].forEach((req) => {
      const reqStatus = this.getStatus(req, context);

      if (!reqStatus) {
        this.request(this.resources[req], context);
      }
    });
    this.request(this.resources[resource], context);
    this.pending.push({
      resource: resource,
      context: context,
      callback: callback,
      error: error
    });

    return;
  }

  refreshPending(err = undefined) {
    const errorCallbackOnAll = this.pending.every((p) => {return p?.error?.toString() !== "function() {}"});
    this.pending.forEach((pend, index) => {
      if (pend === undefined) return;
      const stat = this.getStatus(pend.resource, pend.context);
      if (stat === "cached") {
        let obj = {};

        /* If request was not successful, 'err' will have API error object */
        if (typeof err !== "undefined") {
          obj = err;
        } else {
          obj = this.data[this.resources[pend.resource]][pend.context[this.getContextName(pend.resource)]];
        }
        pend.callback(obj);
        this.pending[index] = undefined;
      }
      if (stat === "error") {
        pend.error(err);
        this.pending[index] = undefined;
      }
    });

    this.pending = this.clean(this.pending, undefined);
    return errorCallbackOnAll;
  };

  private getContextName(resource: string): string {
    if (resource in {"course":1,"lesson":1,"page":1,"component":1}) return resource + "Id";
  }

  private runWatchers(resource: string) {
    let obj = this.data.watchers[resource];
    const params = resource.split(":");
    let data: any;
    if (params[0] === "prop") {
      obj = this.data.watchers.prop[params[1]];
      data = this.data.props[params[1]];
    }

    if (typeof obj !== "undefined") {
      for (const name in obj) {
        if (obj.hasOwnProperty(name)) {
          obj[name](data);
        }
      }

      return true;
    }

    return false;
  }

  public update(resource, context, key, value) {
    if (resource !== 'page') return; // only page implemented so far
    this.data[this.resources[resource]][context[this.getContextName(resource)]][key] = value; // only allowed to set/update a property
  };

  request(resource, context) {
    const collectionPrefix = window.edrnaConfig.resourcePrefix;

    if (!context.courseId) return;
    let url = window.edrnaConfig.host + window.edrnaConfig.serverPrefix + collectionPrefix + resource + "/" + context.courseId + "/";

    if (resource === "courses") {
      this.status[resource][context.courseId] = "pending";
    }

    if (resource === "lessons") {
      url += context.lessonId + "/";
      this.status[resource][context.lessonId] = "pending";
    }
    if (resource === "pages") {
      url += context.pageId + "/";
      this.status[resource][context.pageId] = "pending";
    }
    if (resource === "components") {
      url += context.componentId + "/";
      this.status[resource][context.componentId] = "pending";
    }

    if (url.indexOf("undefined") !== -1) {
      return false;
    }

    const errF = (err) => {
      let key: "courseId" | "lessonId" | "pageId";

      switch(resource) {
        case "courses": key = "courseId"; break;
        case "lessons": key = "lessonId"; break;
        case "pages": key = "pageId"; break;
        default: return;
      }
      const resourceId = context[key];
      this.status[resource][resourceId] = "error";
      const letCallBackHandle404 = this.refreshPending(err);
      if (err.status === 404 && !letCallBackHandle404) {
        console.error('Page do not exist');
        return
      }
    };

    fetch(url, {headers: {'Accept': 'application/json'}}).then((resp) => {
      if (!resp.ok) {
        return Promise.reject('Response not OK'); // Stops the chain;
      }
      return resp.json();
    }).then((data) => {
      if (data.errno) {
        errF(data);
        return;
      }
  
      // Proceed if status is 200 or 304 and no error is present
      if (typeof data.errno === "undefined") {
        const resourceId = context.courseId === "new" ? context.courseId : data._id;
  
        this.data[resource][resourceId] = data;
        // remember the course context on all lesson/page(/component) content
        this.status[resource][resourceId] = "cached" + context.courseId + Date.now();
  
        if (resource === "courses") {
          // This is for the catalog/program/course level styling
          document.body.setAttribute('course-style', this.data[resource][resourceId].course_style);
        }
  
        this.status = this.status;
      } else if (data.errno === 'not-logged') {
        window.location.href = 'logout/';  // Redirect for logout
      }
  
      this.refreshPending();
      this.runWatchers(this.getKeyByValue(this.resources, resource));
    }
  )
  .catch(errF);  // Catch any errors (network errors, etc.)
  }
};

export default StorageService.getSingleton()

export {StorageService as StorageServiceInstance}
